//LIBRARIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { browserHistory, withRouter } from 'react-router';
import styled from 'styled-components';
import MediaQuery from 'react-responsive';

//HELPERS
import { StyledMain, StyledHeaderContainer } from 'helpers/StyledTableComponentsHelper';
import { getTranslatedString, getTranslatedName } from 'helpers/i18n';
import { media, sizes } from 'helpers/MediaHelper';

//COMPONENTS
import useBatchStatus from '../hooks/useBatchStatus';
import DrawerTabs from 'components/ui/DrawerTabs';
import PageHeader from 'components/ui/PageHeader';
import CtaBreadCrumb from 'components/ui/CtaBreadCrumb';
import AntDrawer from 'components/ui/Drawer';
import Loader from 'components/ui/Loader';
import { ButtonPrimary } from '@yuhu-inc/yuhui';
import MobileNavContainer from 'components/ui/MobileNavDropdown/MobileNavContainer';
import MobileNavCurrentTab from 'components/ui/MobileNavDropdown/MobileNavCurrentTab';
import BatchScheduling from '../BatchesPage/BatchScheduling';
import BatchUnscheduling from '../BatchesPage/BatchUnscheduling';
import BatchOverview from './tabs/Overview';
import LinkedPayments from './tabs/LinkedPayments';
import FailedPayments from './tabs/FailedPayments';

import {
  getBatch,
  updateBatch,
  deleteBatch,
  storeBatch,
  scheduleBatch,
  unscheduleBatch,
} from '../store/actions';
import { clearUnits } from 'actions/landlordPropertiesActions';

const StyledCard = styled.div`
  background-color: white;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 24px 0;
  display: flex;
  justify-content: space-between;
`;

const StyledDetailsCard = styled(StyledCard)`
  margin: 0;
  padding-top: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const RestyledMain = styled(StyledMain)`
  display: flex;
  height: 100%;
  justify-content: space-between;
  flex-direction: column;
  overflow-y: auto;
`;

const HeaderContainer = styled(StyledHeaderContainer)`
  flex-direction: row !important;
  padding: 2rem;
  width: 100%;
  max-width: 1800px;
  margin-left: auto;
  margin-right: auto;

  ${media.mobile`
    position: inherit !important;
    padding: 2rem 0;
    margin: inherit;
  `};
`;

const StyledCardHeaderContainer = styled.div`
  padding: 0 32px;
`;

const StyledHeader = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 100%;
  position: inherit !important;
`;

const StyledBackWrapper = styled.div`
  padding: 0px 0 24px;
  ${media.mobile`
    padding: 54px 16px 32px;
  `};
`;

const StyledDrawerTabs = styled(DrawerTabs)`
  overflow: auto !important;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledTabPane = styled(DrawerTabs.TabPane)`
  background-color: white;
`;

const StyledMobileFooter = styled.div`
  background-color: white;
  justify-content: center;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 2;
  padding: 15px;
  box-shadow: 0px -3px 13px rgba(0, 0, 0, 0.0830146);
`;

const FullWidthButtonPrimary = styled(ButtonPrimary)`
  width: 100%;
`;

const TABS = {
  OVERVIEW: 'overview',
  LINKED_PAYMENTS: 'linked_payments',
  FAILED_PAYMENTS: 'failed_payments',
};

const BatchPage = ({
  batch,
  getBatch,
  deleteBatch,
  updateBatch,
  storeBatch,
  scheduleBatch,
  unscheduleBatch,
  routeParams,
  clearUnits,
}) => {
  const [tabIndex, setTabIndex] = useState(TABS.OVERVIEW);
  const [showScheduleDrawer, setShowScheduleDrawer] = useState(false);
  const [showUnscheduleDrawer, setShowUnscheduleDrawer] = useState(false);

  useEffect(() => {
    if (routeParams.batchId) {
      getBatch(routeParams.batchId);
    }
  }, [routeParams.batchId, getBatch]);

  const handleBack = () => {
    clearUnits();
    browserHistory.push('/companies/batches');
  };

  const changeTab = (key) => {
    setTabIndex(key);
  };

  const onUpdateBatch = (data) => {
    storeBatch({ ...batch, ...data });
  };

  const openScheduleDrawer = () => {
    setShowScheduleDrawer(true);
  };

  const closeScheduleDrawer = () => {
    setShowScheduleDrawer(false);
  };

  const openUnscheduleDrawer = () => {
    setShowUnscheduleDrawer(true);
  };

  const closeUnscheduleDrawer = () => {
    setShowUnscheduleDrawer(false);
  };

  const onScheduleBatch = () => {
    getBatch(batch.id);
  };

  const { name, description, status, merchant_subaccount_name, payments_count } = batch;

  const OverviewTab = (
    <BatchOverview
      batch={batch}
      deleteBatch={deleteBatch}
      updateBatch={updateBatch}
      onUpdateBatch={onUpdateBatch}
    />
  );
  const LinkedPaymentsTab = <LinkedPayments batch={batch} />;
  const FailedPaymentsTab = <FailedPayments batch={batch} />;

  const tabs = [
    {
      value: TABS.OVERVIEW,
      label: getTranslatedString('landlordPortal.batches.overview'),
      component: OverviewTab,
    },
    {
      value: TABS.LINKED_PAYMENTS,
      label: getTranslatedString('landlordPortal.batches.linkedPayments'),
      component: LinkedPaymentsTab,
    },
    {
      value: TABS.FAILED_PAYMENTS,
      label: getTranslatedString('landlordPortal.batches.failedPayments'),
      component: FailedPaymentsTab,
    },
  ];

  const ScheduleDrawer = (
    <AntDrawer
      maskClosable={false}
      keyboard
      visible={showScheduleDrawer}
      onClose={closeScheduleDrawer}
      closable={false}
      width={600}
    >
      <BatchScheduling
        batchId={batch.id}
        formData={{ post_at: batch.post_at }}
        handleClose={closeScheduleDrawer}
        onSubmissionComplete={onScheduleBatch}
        scheduleBatch={scheduleBatch}
        batchStatus={batch.status}
      />
    </AntDrawer>
  );

  const UnscheduleDrawer = (
    <AntDrawer
      maskClosable={false}
      keyboard
      visible={showUnscheduleDrawer}
      onClose={closeUnscheduleDrawer}
      closable={false}
      width={600}
    >
      <BatchUnscheduling
        batchId={batch.id}
        handleClose={closeUnscheduleDrawer}
        onSubmissionComplete={onScheduleBatch}
        unscheduleBatch={unscheduleBatch}
      />
    </AntDrawer>
  );

  const translatedName = getTranslatedName(name);
  const translatedDescription = getTranslatedName(description);

  const ScheduleButton = payments_count > 0 && (
    <ButtonPrimary contentKey="landlordPortal.batches.scheduleBatch" onClick={openScheduleDrawer} />
  );

  const ScheduleButtonMobile = payments_count > 0 && (
    <FullWidthButtonPrimary
      contentKey="landlordPortal.batches.scheduleBatch"
      onClick={openScheduleDrawer}
    />
  );

  return (
    <RestyledMain>
      <HeaderContainer>
        <StyledHeader>
          <StyledBackWrapper>
            <CtaBreadCrumb
              onClick={handleBack}
              breadcrumbBackTitle={getTranslatedString('landlordPortal.batches.paymentBatches')}
              breadCrumbCurrentTitle={translatedName}
            />
          </StyledBackWrapper>
          <PageHeader
            name={translatedName}
            status={status}
            description={translatedDescription}
            accountName={merchant_subaccount_name}
          >
            <MediaQuery query={`(min-device-width: ${sizes.ipadMin}px)`}>
              <StyledCardHeaderContainer>
                {useBatchStatus(
                  status,
                  ScheduleButton,
                  <ButtonPrimary
                    contentKey="landlordPortal.batches.unscheduleBatch"
                    onClick={openUnscheduleDrawer}
                  />
                )}
              </StyledCardHeaderContainer>
            </MediaQuery>
          </PageHeader>
          <MediaQuery query={`(max-device-width: ${sizes.ipadMax}px)`}>
            <MobileNavContainer tabIndex={tabIndex} tabs={tabs} onChange={changeTab} />
          </MediaQuery>
          <StyledDetailsCard>
            <Loader name={name} />
            <MediaQuery query={`(max-device-width: ${sizes.ipadMax}px)`}>
              <MobileNavCurrentTab tabIndex={tabIndex} tabs={tabs} />
            </MediaQuery>
            <MediaQuery query={`(min-device-width: ${sizes.ipadMin}px)`}>
              <StyledDrawerTabs
                defaultActiveKey={tabIndex}
                onChange={changeTab}
                color="inherit"
                padding="0 32px"
              >
                <StyledTabPane
                  tab={getTranslatedString('landlordPortal.batches.overview')}
                  key={TABS.OVERVIEW}
                >
                  {OverviewTab}
                </StyledTabPane>
                <StyledTabPane
                  tab={getTranslatedString('landlordPortal.batches.linkedPayments')}
                  key={TABS.LINKED_PAYMENTS}
                  padding="32px 0 0 0"
                >
                  {tabIndex === TABS.LINKED_PAYMENTS && LinkedPaymentsTab}
                </StyledTabPane>
                <StyledTabPane
                  tab={getTranslatedString('landlordPortal.batches.failedPayments')}
                  key={TABS.FAILED_PAYMENTS}
                >
                  {tabIndex === TABS.FAILED_PAYMENTS && FailedPaymentsTab}
                </StyledTabPane>
              </StyledDrawerTabs>
            </MediaQuery>
          </StyledDetailsCard>
        </StyledHeader>
      </HeaderContainer>
      {useBatchStatus(status, ScheduleDrawer, UnscheduleDrawer)}
      <MediaQuery query={`(max-device-width: ${sizes.ipadMax}px)`}>
        {useBatchStatus(
          status,
          <StyledMobileFooter>{ScheduleButtonMobile}</StyledMobileFooter>,
          <StyledMobileFooter>
            <FullWidthButtonPrimary
              contentKey="landlordPortal.batches.unscheduleBatch"
              onClick={openUnscheduleDrawer}
            />
          </StyledMobileFooter>
        )}
      </MediaQuery>
    </RestyledMain>
  );
};

const mapStoreToProps = (store) => ({
  batch: store.landlord.batches.selectedBatch,
});

const mapDispatchToProps = {
  getBatch,
  updateBatch,
  deleteBatch,
  storeBatch,
  scheduleBatch,
  unscheduleBatch,
  clearUnits,
};

export default withRouter(connect(mapStoreToProps, mapDispatchToProps)(BatchPage));
